const openpathConfig = require('openpathConfig');

/**
 * Determines which Locize version to save missing translations to.
 *
 * Only dev environment should save missing translations to "latest".
 * Production environments (prod/prodeu1) should NOT save - they only read from stable "production" version.
 * This ensures:
 * - Control over what goes to production (no accidental untranslated strings)
 * - Clear review step (translators work in "latest", then copy to "production" when ready)
 * - Stability in production (only reviewed translations are used)
 *
 * Workflow:
 * 1. Dev pushes missing strings → "latest"
 * 2. Translators work in "latest" version
 * 3. When ready, manually copy "latest" → "production" in Locize UI
 * 4. Production environments automatically get new translations from "production"
 */
const getSaveMissingVersion = () => {
  // Use opConfig.ENV instead of process.env.OP_ENV (which is undefined when file loads)
  const opEnv = openpathConfig.ENV || '';

  // Only dev should save missing translations
  // Production environments should NOT save - they only read from stable "production"
  if (opEnv === 'dev') {
    // Dev: save to "latest" (must be writable in Locize)
    return 'latest';
  }

  // Don't save from production environments, sandbox, or mine
  // Production environments only read from "production" version
  return undefined;
};

const saveMissingVersion = getSaveMissingVersion();

// i18next-locize-backend by default only allows saving from localhost
// We need to explicitly allow the dev hostname for saveMissing to work
// This is REQUIRED - without it, saveMissing will not work even if saveMissing: true
const getAllowedHosts = () => {
  const opEnv = openpathConfig.ENV || '';

  if (opEnv === 'dev') {
    // Get current hostname if in browser context
    const currentHostname =
      typeof window !== 'undefined' ? window.location.hostname : null;

    // Allow localhost, common dev domains, and current hostname
    const allowed = ['localhost', '127.0.0.1', 'helium.dev.openpath.com'];

    // Add current hostname if it's different and not already in the list
    if (currentHostname && !allowed.includes(currentHostname)) {
      allowed.push(currentHostname);
    }

    return allowed;
  }

  // For other environments, don't allow saving (return undefined)
  return undefined;
};

const allowedHosts = getAllowedHosts();

export const locizeOptions = {
  projectId: openpathConfig.LOCIZE_PROJECTID,
  apiKey: openpathConfig.LOCIZE_APIKEY,
  version: openpathConfig.LOCIZE_VERSION,
  referenceLng: openpathConfig.LOCIZE_REFLNG,
  // CRITICAL: saveMissingTo tells Locize backend WHERE to save missing keys
  // Without this, saveMissing: true will not trigger any API calls
  ...(saveMissingVersion && { saveMissingTo: saveMissingVersion }),
  // CRITICAL: allowedAddOrUpdateHosts tells Locize backend WHICH hostnames are allowed
  // By default, it only allows localhost - if you're on a different host, saves are blocked
  // Without this, saveMissing: true will not trigger any API calls from non-localhost hosts
  ...(allowedHosts && { allowedAddOrUpdateHosts: allowedHosts }),
};
